import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  List,
  ListItem,
  VStack,
} from '@chakra-ui/react';

import Callout from 'components/_core/Callout';
import IconButton from 'components/_core/IconButton';
import Modal from 'components/_core/Modal';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { createToast } from 'features/Bento/atoms';
import { calcApportionedShares } from 'features/Onboarding/modules/Beneficiaries/libs/calcApportionnedShares';

import { locales } from './locales';
import { APPLICATION_FOLLOW_UP_STEPS } from './statuses';
import { ApplicationStep, ApplicationStepCardBody } from './types';

const ApplicationFundsBody: ApplicationStepCardBody = ({
  data: { capitalDeposit, companyProfile, legalBeneficiaries, userProfile },
  stepStatus,
}) => {
  const [isIbanDialogOpen, setIsIbanDialogOpen] = useState(false);

  const BANKING_INFO = [
    {
      label: locales.applicationFunds.bankingInfo.transferLabel,
      value:
        legalBeneficiaries.length > 1
          ? `Shine - Prénom Nom - ${companyProfile.legalName}`
          : `Shine - ${userProfile.firstName} ${userProfile.lastName} - ${companyProfile.legalName}`,
    },
    {
      label: locales.applicationFunds.bankingInfo.recipientName,
      value: 'Étude notariale Quentin Fourez',
    },
    {
      label: locales.applicationFunds.bankingInfo.iban,
      value: 'FR7640031002700000453361H76',
    },
    {
      label: locales.applicationFunds.bankingInfo.bic,
      value: 'CDCGFRPPXXX',
    },
    {
      label: locales.applicationFunds.bankingInfo.bankName,
      value: 'DDFIP EURE',
    },
    {
      label: locales.applicationFunds.bankingInfo.bankAddress,
      value: 'Bd Georges Chauvin, 27023 Evreux CEDEX',
    },
  ] satisfies Array<{ label: string; value: string }>;

  return (
    <>
      <VStack align="stretch" spacing="space-24">
        <Text>{locales.applicationFunds.body}</Text>

        <Box borderRadius="radius-10" overflow="hidden">
          {legalBeneficiaries.map((beneficiary, index) => {
            const { totalAmountReleasedEuros } = calcApportionedShares(
              {
                legalNumberOfShares: companyProfile.legalNumberOfShares,
                legalShareCapital: companyProfile.legalShareCapital,
                paidUpCapitalPercentage: capitalDeposit.paidUpCapitalPercentage,
              },
              beneficiary.numberOfShares,
            );

            return (
              <Flex
                backgroundColor={index % 2 === 0 ? 'grey.100' : 'white'}
                justify="space-between"
                key={beneficiary.companyLegalBeneficiaryId}
                padding="space-16"
              >
                <Text color="grey.700">
                  {beneficiary.firstName} {beneficiary.lastName}
                  {beneficiary.isCurrentUser
                    ? ` ${locales.applicationFunds.you}`
                    : ''}
                </Text>

                <Text color="grey.800">{totalAmountReleasedEuros}</Text>
              </Flex>
            );
          })}
        </Box>

        {stepStatus === 'active' ? (
          <Button
            alignSelf="flex-start"
            onClick={() => setIsIbanDialogOpen(true)}
            variant="inline-secondary"
          >
            {locales.applicationFunds.displayBankingInfo}
          </Button>
        ) : null}
      </VStack>

      <Modal
        isOpen={isIbanDialogOpen}
        onClose={() => setIsIbanDialogOpen(false)}
      >
        <Modal.Header></Modal.Header>

        <Modal.Body>
          <Modal.Title>{locales.applicationFunds.modalTitle}</Modal.Title>

          <Callout hideIcon marginY="space-32" variant="info">
            <List listStyleType="disc" paddingLeft="space-16" spacing="space-4">
              <ListItem>
                <Text>{locales.applicationFunds.modalText['0']}</Text>
              </ListItem>
              <ListItem>
                <Text>{locales.applicationFunds.modalText['1']}</Text>
              </ListItem>
            </List>
          </Callout>

          <VStack align="stretch" divider={<Divider />} spacing="space-16">
            {BANKING_INFO.map(({ label, value }) => (
              <Box key={label}>
                <Text color="grey.800" marginBottom="space-4" size="sm">
                  {label}
                </Text>

                <Text alignItems="center" color="grey.600" display="flex">
                  <span>{value}</span>

                  <IconButton
                    aria-label={locales.applicationFunds.copy}
                    color="grey.600"
                    icon="copy"
                    onClick={() => {
                      window.navigator.clipboard.writeText(value).then(() => {
                        createToast({
                          status: 'success',
                          title: locales.applicationFunds.copied,
                        });
                      });
                    }}
                    variant="inline-secondary"
                  />
                </Text>
              </Box>
            ))}
          </VStack>

          <Divider marginY="space-16" />
          <Button
            marginTop="space-16"
            onClick={() => {
              window.navigator.clipboard
                .writeText(
                  BANKING_INFO.map(
                    ({ label, value }) => `${label} : ${value}`,
                  ).join('\n'),
                )
                .then(() => {
                  createToast({
                    status: 'success',
                    title: locales.applicationFunds.copied,
                  });
                });
            }}
            rightIcon={<SunshineIcon name="copy" />}
            variant="secondary"
            width="100%"
          >
            {locales.applicationFunds.copyAll}
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export const applicationFundsStep = {
  activeBadgeLabel: locales.activeBadgeLabel.todo,

  body: ApplicationFundsBody,

  isActive: ({ capitalDeposit }) =>
    APPLICATION_FOLLOW_UP_STEPS[capitalDeposit.status] === 'funds',

  title: locales.applicationFunds.title,
} satisfies ApplicationStep;
